import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

/**
 * Pill badge showing whether a court category is indoor ("Halle") or
 * outdoor ("Außen"). Set `onSelected` when rendered on a selected (blue)
 * surface: both badges switch to a passive translucent variant that keeps
 * their hue (white for indoor, amber for outdoor).
 */
@Component({
  selector: 'app-category-env-badge',
  standalone: true,
  template: `
    <span
      class="env-badge"
      [class.indoor]="isIndoor"
      [class.outdoor]="!isIndoor"
      [class.on-selected]="onSelected">
      @if (isIndoor) {
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="M3 11 12 4l9 7" />
          <path d="M5.5 9.5V20h13V9.5" />
        </svg>
        Halle
      } @else {
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <line x1="12" y1="2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
          <line x1="4.9" y1="4.9" x2="7" y2="7" />
          <line x1="17" y1="17" x2="19.1" y2="19.1" />
          <line x1="4.9" y1="19.1" x2="7" y2="17" />
          <line x1="17" y1="7" x2="19.1" y2="4.9" />
        </svg>
        Außen
      }
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-flex;
      }

      .env-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.03em;
        padding: 3px 8px;
        border-radius: 999px;
        white-space: nowrap;
      }

      .env-badge.outdoor {
        background: #fef3c7;
        color: #b45309;
      }

      .env-badge.indoor {
        background: #e0f2fe;
        color: #0369a1;
      }

      /* On a selected (blue) surface both badges switch to the same passive
         translucent pill; the outdoor badge keeps its hue via soft amber
         text/icon (a translucent amber background would blend into olive
         on the blue card). */
      .env-badge.on-selected {
        background: rgba(255, 255, 255, 0.22);
        color: #fff;
      }

      .env-badge.outdoor.on-selected {
        color: #fde68a;
      }
    `,
  ],
})
export class CategoryEnvBadgeComponent {
  /** true ⇒ "Halle" (indoor), false ⇒ "Außen" (outdoor) */
  @Input() isIndoor = false;

  /** true when rendered on a selected (blue) surface */
  @Input() onSelected = false;
}
